import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AUTH_ROUTES,
  DEFAULT_POST_LOGIN_PATH,
  toSafeRedirectPath,
  type CurrentUser,
} from "@jipraks/shared";
import { AuthContext, type AuthContextValue, type AuthStatus } from "@client/hooks/use-auth";
import { api } from "@client/lib/api-client";
import { readStoredViewMode, storeViewMode, type ViewMode } from "@client/lib/view-mode-storage";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUserState] = useState<CurrentUser | null>(null);
  const [storedMode, setStoredMode] = useState<ViewMode>(readStoredViewMode);

  useEffect(() => {
    let isCancelled = false;
    api.api.me
      .$get()
      .then(async (response) => {
        if (isCancelled) return;
        if (!response.ok) {
          setStatus("anonymous");
          return;
        }
        const body = await response.json();
        setUserState(body.data);
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        console.warn("Could not load the current user", error);
        if (!isCancelled) setStatus("anonymous");
      });
    return () => {
      isCancelled = true;
    };
  }, []);

  const setUser = useCallback((next: CurrentUser) => {
    setUserState(next);
    setStatus("authenticated");
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    storeViewMode(mode);
    setStoredMode(mode);
  }, []);

  const signIn = useCallback((redirectPath?: string) => {
    const target = toSafeRedirectPath(redirectPath, DEFAULT_POST_LOGIN_PATH);
    window.location.assign(`${AUTH_ROUTES.GOOGLE_START}?redirect=${encodeURIComponent(target)}`);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.api.auth.logout.$post();
    } finally {
      setUserState(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      viewMode: user?.isAdmin ? storedMode : "user",
      setViewMode,
      signIn,
      signOut,
      setUser,
    }),
    [status, user, storedMode, setViewMode, signIn, signOut, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
